import { useCurrentEditor } from '@tiptap/react';
import { useEffect, useRef } from 'react';

import { BaseEditor, type BaseEditorProps } from './BaseEditor';
import { MarkdownExtension } from './extensions';

import type { EditorEvents } from '@tiptap/core';

interface MarkdownEditorProps extends Omit<
  BaseEditorProps<string>,
  'onUpdate'
> {
  /** Initial markdown content to display in the editor */
  initialContent?: string;
  /** Replaces editor content when this version changes. */
  syncedContent?: string;
  syncedContentVersion?: number | string;
  focusOnSyncedContent?: boolean;
  syncedFocusOffset?: { x: number; y: number } | null;
  /** Callback fired when editor content changes, receives markdown string */
  onUpdate?: (markdown: string) => void;
}

function MarkdownEditorContentSync({
  content,
  version,
  focusOnSync,
  focusOffset,
}: {
  content?: string;
  version?: number | string;
  focusOnSync?: boolean;
  focusOffset?: { x: number; y: number } | null;
}) {
  const { editor } = useCurrentEditor();
  const lastSyncedVersionRef = useRef<number | string | undefined>(undefined);

  useEffect(() => {
    if (
      !editor ||
      version === undefined ||
      lastSyncedVersionRef.current === version
    ) {
      return;
    }

    lastSyncedVersionRef.current = version;
    editor.commands.setContent(content ?? '', { emitUpdate: false });

    if (!focusOnSync) return;

    if (!focusOffset) {
      requestAnimationFrame(() => editor.commands.focus('start'));
      return;
    }

    let cancelled = false;
    const frameIds: number[] = [];
    const timeoutIds: number[] = [];

    const focusAtPreviewOffset = () => {
      if (cancelled) return true;

      const editorRect = editor.view.dom.getBoundingClientRect();
      if (editorRect.width === 0 || editorRect.height === 0) return false;

      const left = Math.min(
        editorRect.right - 1,
        Math.max(editorRect.left + 1, editorRect.left + focusOffset.x)
      );
      const top = Math.min(
        editorRect.bottom - 1,
        Math.max(editorRect.top + 1, editorRect.top + focusOffset.y)
      );
      const position = editor.view.posAtCoords({ left, top });

      if (!position) return false;

      editor.chain().focus().setTextSelection(position.pos).run();
      return true;
    };

    const scheduleFrameAttempt = (frames: number) => {
      const tick = (remainingFrames: number) => {
        const frameId = requestAnimationFrame(() => {
          if (remainingFrames <= 1) {
            focusAtPreviewOffset();
            return;
          }
          tick(remainingFrames - 1);
        });
        frameIds.push(frameId);
      };
      tick(frames);
    };

    scheduleFrameAttempt(2);
    scheduleFrameAttempt(4);
    timeoutIds.push(
      window.setTimeout(() => {
        if (!focusAtPreviewOffset()) editor.commands.focus('start');
      }, 120)
    );

    return () => {
      cancelled = true;
      frameIds.forEach((frameId) => cancelAnimationFrame(frameId));
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [content, editor, focusOffset, focusOnSync, version]);

  return null;
}

export const MarkdownEditor = ({
  onUpdate,
  additionalExtensions = [],
  syncedContent,
  syncedContentVersion,
  focusOnSyncedContent,
  syncedFocusOffset,
  ...props
}: MarkdownEditorProps) => {
  const update = ({ editor }: EditorEvents['update']) =>
    // @ts-ignore
    onUpdate?.(editor.storage.markdown.getMarkdown());

  return (
    <BaseEditor
      {...props}
      onUpdate={update}
      additionalExtensions={[MarkdownExtension, ...additionalExtensions]}
    >
      <MarkdownEditorContentSync
        content={syncedContent}
        version={syncedContentVersion}
        focusOnSync={focusOnSyncedContent}
        focusOffset={syncedFocusOffset}
      />
    </BaseEditor>
  );
};
